import { Link, useNavigate } from 'react-router-dom';
import NationalHome from '../components/NationalHome';
import { useEffect, useState } from 'react';
import {
  Search, Truck, ShieldCheck, ArrowRight, RefreshCw, ShoppingBag, Siren, MapPin, Gavel,
} from 'lucide-react';
import { CATEGORIES } from '../lib/categories';
import EssentialsGrid, { VoiceOrderHero } from '../components/EssentialsGrid';
import { stores as storesApi, orders as ordersApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useCart } from '../lib/cart';
import StoreCard from '../components/StoreCard';
import VoiceOrderLauncher from '../components/VoiceOrderLauncher';
import SecurityNotice from '../components/SecurityNotice';
import ReferAFriendButton from '../components/ReferAFriendButton';

/**
 * Home — Needs-First edition.
 *
 * No carousels, no flashy hero, no promo banners. High-contrast, text-first,
 * fast-loading on 2G/3G phones. Utility ordering: Search → Reorder → Nearby
 * shops → Categories → Why-trust footer.
 */



export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [q, setQ] = useState('');
  const [lastBasket, setLastBasket] = useState(null);
  const [reordering, setReordering] = useState(false);
  const { user } = useAuth();
  const { replaceCart } = useCart();
  const nav = useNavigate();

  useEffect(() => { storesApi.list().then(setFeatured).catch(() => {}); }, []);

  useEffect(() => {
    if (!user || user.role !== 'customer') return;
    ordersApi.lastBasket()
      .then(d => (d && d.has_basket && d.available_count > 0 ? d : null))
      .then(setLastBasket)
      .catch(() => {});
  }, [user]);

  const onReorder = async () => {
    if (!lastBasket || reordering) return;
    setReordering(true);
    try {
      const { store } = await storesApi.get(lastBasket.store_id);
      const items = (lastBasket.items || [])
        .filter(it => it.available)
        .map(it => ({
          product: {
            id: it.product_id, name: it.name, price: it.price,
            unit: it.unit, image_url: it.image_url, store_id: it.store_id,
          },
          qty: it.qty || 1,
        }));
      if (!items.length) { setReordering(false); return; }
      replaceCart(store, items);
      nav('/cart');
    } catch { setReordering(false); }
  };

  const onSearch = (e) => { e.preventDefault(); if (q.trim()) nav(`/search?q=${encodeURIComponent(q)}`); };

  return (
    <div className="bg-white">
      {/* --- Search + Emergency (utility-first, no hero) --- */}
      <section className="max-w-3xl mx-auto px-4 md:px-6 pt-6 pb-4">
        <form onSubmit={onSearch} className="flex items-center gap-2 rounded-lg border-2 border-ink bg-white pl-4 pr-2 py-2">
          <Search className="w-5 h-5 text-ink shrink-0"/>
          <input
            data-testid="home-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="What do you need? e.g. atta, milk, paracetamol"
            className="flex-1 outline-none text-base bg-transparent placeholder:text-ink/40 min-w-0"
            autoComplete="off"
          />
          <button
            data-testid="home-search-submit"
            className="inline-flex items-center gap-1 rounded-md bg-ink text-white font-black uppercase tracking-wider text-xs px-4 h-10 shrink-0"
          >
            Find <ArrowRight className="w-4 h-4"/>
          </button>
        </form>

        <div className="mt-3 flex items-center justify-between text-xs gap-2 flex-wrap">
          <Link
            to="/tender/new"
            data-testid="home-tender-cta"
            className="inline-flex items-center gap-1 font-black uppercase tracking-wider text-[#1B5E20] hover:underline"
          >
            <Gavel className="w-3.5 h-3.5"/>Ask shops for a price
          </Link>
          {user?.role === 'customer' && <ReferAFriendButton variant="outline"/>}
          <Link
            to="/emergency/history"
            data-testid="home-emergency-link"
            className="inline-flex items-center gap-1 font-black uppercase tracking-wider text-[#B71C1C] hover:underline"
          >
            <Siren className="w-3.5 h-3.5"/>My SOS history
          </Link>
        </div>
      </section>

      <VoiceOrderHero/>
      <NationalHome/>
      <EssentialsGrid/>

      {/* --- Reorder utility banner --- */}
      {lastBasket && (
        <section className="max-w-3xl mx-auto px-4 md:px-6 py-2">
          <div
            data-testid="reorder-basket-banner"
            className="rounded-lg border-2 border-[#1B5E20] bg-[#E8F5E9] p-3 flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-md bg-[#1B5E20] text-white grid place-items-center shrink-0">
              <ShoppingBag className="w-5 h-5"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[#1B5E20]">One-tap reorder</div>
              <div className="font-heading font-black text-base mt-0.5 truncate text-ink">
                {lastBasket.available_count} item{lastBasket.available_count === 1 ? '' : 's'} from {lastBasket.store_name || 'your shop'}
              </div>
            </div>
            <button
              data-testid="reorder-basket-btn"
              onClick={onReorder}
              disabled={reordering}
              className="inline-flex items-center gap-1 rounded-md bg-[#1B5E20] hover:bg-[#0D3F12] disabled:opacity-60 text-white font-black uppercase text-xs tracking-wider px-3 h-9"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${reordering ? 'animate-spin' : ''}`}/>
              {reordering ? 'Adding…' : 'Reorder'}
            </button>
          </div>
        </section>
      )}

      {/* --- Emergency call-out (compact, honest, no gradient) --- */}
      <section className="max-w-3xl mx-auto px-4 md:px-6 pt-2 pb-2">
        <div
          data-testid="emergency-callout"
          className="rounded-lg border-2 border-[#B71C1C] bg-[#FFEBEE] p-3 flex items-start gap-3"
        >
          <Siren className="w-6 h-6 text-[#B71C1C] shrink-0 mt-0.5"/>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[#B71C1C]">Need a medicine urgently?</div>
            <div className="font-heading font-black text-base text-ink mt-0.5">
              Tap the red <b>SOS</b> button (bottom-right) — it alerts every pharmacy within 5 km.
            </div>
          </div>
        </div>
      </section>


      {/* --- Security notice — announces phone-verification purpose --- */}
      <SecurityNotice/>

      {/* --- Sign Up CTA — only for logged-out visitors --- */}
      {!user && <HomeSignUpCta/>}

      {/* --- Category grid (compact, text-first, no images) --- */}
      <section className="max-w-3xl mx-auto px-4 md:px-6 py-4">
        <div className="text-[10px] uppercase tracking-[0.2em] font-black text-ink mb-2">Shop by category</div>
        <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
          {CATEGORIES.map(({ id, label, Icon, bg, fg }) => (
            <Link
              key={id}
              data-testid={`category-tile-${id}`}
              to={`/stores?category=${id}`}
              className="flex items-center gap-2 rounded-lg border-2 border-ink/10 hover:border-ink transition px-3 py-3 min-h-[52px]"
              style={{ backgroundColor: bg }}
            >
              <Icon className="w-4 h-4 shrink-0" style={{ color: fg }}/>
              <span className="text-xs md:text-sm font-black uppercase tracking-wider truncate" style={{ color: fg }}>
                {label}
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* --- Send a raw voice note to a chosen shop's WhatsApp --- */}
      <VoiceOrderLauncher/>

      {/* --- Featured local stores (kept, but text-heavy) --- */}
      <section className="max-w-3xl mx-auto px-4 md:px-6 py-4">
        <div className="flex items-end justify-between gap-4 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-black text-ink">Around your village</div>
            <div className="font-heading font-black text-xl mt-0.5">Trusted local shops</div>
          </div>
          <Link
            to="/stores"
            data-testid="see-all-stores"
            className="text-xs font-black uppercase tracking-wider text-ink hover:underline inline-flex items-center gap-1"
          >
            See all <ArrowRight className="w-3 h-3"/>
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-3">
          {featured.slice(0, 4).map(s => <StoreCard key={s.id} store={s}/>)}
        </div>
      </section>

      {/* --- Why-trust footer (compact icons, high-contrast) --- */}
      <section className="max-w-3xl mx-auto px-4 md:px-6 py-6 border-t-2 border-ink/10 mt-4">
        <div className="grid grid-cols-3 gap-3">
          {[
            { Icon: Truck,       t: 'Fast delivery',    d: '30–60 min' },
            { Icon: ShieldCheck, t: 'COD & UPI',        d: 'No card needed' },
            { Icon: MapPin,      t: 'Your gali only',   d: '5 km radius' },
          ].map(({ Icon, t, d }) => (
            <div key={t} className="text-center">
              <Icon className="w-5 h-5 mx-auto text-ink"/>
              <div className="mt-1 text-xs font-black uppercase tracking-wider text-ink">{t}</div>
              <div className="text-[11px] text-ink/60">{d}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function HomeSignUpCta() {
  return (
    <section className="max-w-3xl mx-auto px-4 md:px-6 py-4" data-testid="home-signup-cta">
      <div className="rounded-2xl border-2 border-[#E67E22] bg-gradient-to-br from-[#FFF7EE] to-white p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[#D35400]">New to BharatCart?</div>
          <div className="font-heading font-black text-xl md:text-2xl mt-1">
            Get your account in 30 seconds — phone <span className="text-[#E67E22]">or email</span>.
          </div>
          <div className="mt-1 text-xs md:text-sm text-ink/70">
            Order groceries, keep a digital Khata, get medicine urgently, or sell your own shop.
          </div>
        </div>
        <button data-testid="home-signup-btn"
          onClick={() => window.dispatchEvent(new CustomEvent('mandispot:open-auth', { detail: { mode: 'signup' } }))}
          className="btn-primary !py-3 !px-6 whitespace-nowrap">
          Sign Up — it&apos;s free
        </button>
      </div>
    </section>
  );
}
